import { Action, Reducer } from "redux";
import { DeepClone } from "../../helperFunctions";
import { AppThunkAction } from "..";
import { remoteNOActions } from ".";

export const FINANCE_TYPE_CHANGED = "FINANCE_TYPE_CHANGED";
export const FINANCE_COMPANY_CHANGED = "FINANCE_COMPANY_CHANGED";
export const FINANCE_TYPE_VALID = "FINANCE_TYPE_VALID";
export const FINANCE_TYPE_INVALID = "FINANCE_TYPE_INVALID";
export const FINANCE_COMPANY_VALID = "FINANCE_COMPANY_VALID";
export const FINANCE_COMPANY_INVALID = "FINANCE_COMPANY_INVALID";

export interface FinancingTypesState {
  sectionHeader: string;
  selectedFinanceTypeId?: string;
  selectedCompanyId?: string;
  isFinanceTypeValid?: boolean;
  isFinanceCompanyValid?: boolean;
  financeTypeRequiredErrorMessage?: string;
  financeCompanyRequiredErrorMessage?: string;
  financingTypes?: IFinancingType[];
}

// Leasing, Loan or no-financing
export interface IFinancingType {
  id: string;
  name: string;
  selected: boolean;
  texts: IFinancingText;
  companies?: IFinancingCompany[];
}

export interface IFinancingCompany {
  selected: boolean;
  name: string;
  id: string;
}

export interface IFinancingText {
  header: string;
  description?: string;
}

export interface IFinanceActionData {
  financeTypeId: string | undefined;
  financeCompanyId: string | undefined;
}

export type FinanceAction = {
  type:
    | typeof FINANCE_TYPE_CHANGED
    | typeof FINANCE_COMPANY_CHANGED
    | typeof FINANCE_TYPE_VALID
    | typeof FINANCE_TYPE_INVALID
    | typeof FINANCE_COMPANY_VALID
    | typeof FINANCE_COMPANY_INVALID;
  data: IFinanceActionData | string | boolean;
};
// for dev. purposes
const defaults = {
  sectionHeader: "", //"Finansiering",
  selectedFinanceTypeId: undefined,
  selectedCompanyId: undefined,
  financeTypeRequiredErrorMessage: "", // "Vennligst velg finansieringsmåte.",
  financeCompanyRequiredErrorMessage: "", // "Vennligst velg finansieringsleverandør.",
  financingTypes: [
    {
      id: "0",
      selected: false,
      texts: {
        header: "", //"Leasing",
        description: "", //"Velg dette dersom du finansierer bilen gjennom en leasingavtale",
      },
      companies: [
        {
          selected: false,
          name: "Company 1",
          id: "0",
        },
        {
          selected: false,
          name: "Company 2",
          id: "1",
        },
      ],
    },
    {
      id: "1",
      selected: false,
      texts: {
        header: "", //"Lån",
        description: "", //"Velg dette dersom du finansierer bilen gjennom en låneavtale med pant i bilen",
      },
      companies: [
        {
          selected: false,
          name: "Company 1",
          id: "0",
        },
        {
          selected: false,
          name: "Company 2",
          id: "1",
        },
      ],
    },
    {
      id: "2",
      selected: false,
      texts: {
        header: "", //"Ingen finansiering",
        //description: "No financing description",
      },
    },
  ],
} as FinancingTypesState;

export const actions = {
  financeTypeChanged:
    (value: IFinanceActionData): AppThunkAction<FinanceAction | {}> =>
    dispatch => {
      dispatch({
        type: FINANCE_TYPE_CHANGED,
        data: value as IFinanceActionData,
      });
      dispatch(remoteNOActions.requestPriceData());
    },
  financeCompanyChanged:
    (value: unknown): AppThunkAction<FinanceAction | {}> =>
    dispatch => {
      dispatch({
        type: FINANCE_COMPANY_CHANGED,
        data: value as IFinanceActionData,
      });
    },
};

export function validateSelectedFinanceType(financeTypeState?: FinancingTypesState): boolean {
  const selectedFinanceTypeId: string | undefined = financeTypeState?.selectedFinanceTypeId;

  if (selectedFinanceTypeId) {
    const financeTypeId = financeTypeState?.financingTypes?.find(x => x.id === selectedFinanceTypeId);
    if (financeTypeId) {
      return true;
    }
  } else {
    return false;
  }
  return false;
}

export function validateSelectedCompany(financeTypeState?: FinancingTypesState): boolean {
  const selectedFinanceTypeId: string | undefined = financeTypeState?.selectedFinanceTypeId;
  const selectedFinanceCompanyId: string | undefined = financeTypeState?.selectedCompanyId;
  let isSelectedCompanyIdValid = false;
  if (selectedFinanceTypeId) {
    if (selectedFinanceTypeId === "0" || selectedFinanceTypeId === "1") {
      const financeType = financeTypeState?.financingTypes?.find(x => x.id === selectedFinanceTypeId);
      const selectedCompanyId = financeType?.companies?.find(x => x.id === selectedFinanceCompanyId);

      if (selectedCompanyId) {
        isSelectedCompanyIdValid = true;
      }
    } else {
      // Case: no financing
      isSelectedCompanyIdValid = true;
    }
  }

  return isSelectedCompanyIdValid;
}

export const reducer: Reducer<FinancingTypesState> = (state: FinancingTypesState = defaults, input: Action): FinancingTypesState => {
  const action = input as FinanceAction;

  switch (action?.type) {
    case FINANCE_TYPE_CHANGED: {
      return handleFinanceTypeChange(state, (action.data as IFinanceActionData).financeTypeId);
    }
    case FINANCE_COMPANY_CHANGED: {
      return handleFinanceCompanyChange(state, (action.data as IFinanceActionData).financeCompanyId);
    }
    case FINANCE_TYPE_VALID: {
      if (state.isFinanceTypeValid === true) {
        return state;
      }

      return {
        ...state,
        isFinanceTypeValid: true,
      };
    }
    case FINANCE_TYPE_INVALID: {
      if (state.isFinanceTypeValid === false) {
        return state;
      }
      return {
        ...state,
        isFinanceTypeValid: false,
      };
    }
    case FINANCE_COMPANY_VALID: {
      if (state.isFinanceCompanyValid === true) {
        return state;
      }
      return {
        ...state,
        isFinanceCompanyValid: true,
      };
    }
    case FINANCE_COMPANY_INVALID: {
      if (state.isFinanceCompanyValid === false) {
        return state;
      }
      return {
        ...state,
        isFinanceCompanyValid: false,
      };
    }
    default:
      return state;
  }
};

function handleFinanceTypeChange(state: FinancingTypesState, selectedFinanceId: string | undefined): FinancingTypesState {
  //Do not edit provided state directly! State should be immutable!
  //Clone state and do changes there!
  let stateClone = DeepClone(state);
  const finalSelectedFinanceId = selectedFinanceId;
  const financeType = getFinanceTypeById(stateClone, finalSelectedFinanceId);
  if (financeType) {
    setSelectedFinancingType(stateClone, financeType);
  }

  // reset back all pre-selected companies
  stateClone?.financingTypes?.forEach(x => x.companies?.forEach(y => (y.selected = false)));

  if (!stateClone.isFinanceTypeValid) {
    stateClone = {
      ...stateClone,
      isFinanceTypeValid: true,
    };
  }

  if (finalSelectedFinanceId !== "2") {
    stateClone = {
      ...stateClone,
      isFinanceCompanyValid: false,
    };
  } else {
    stateClone = {
      ...stateClone,
      isFinanceCompanyValid: true,
    };
  }

  stateClone = {
    ...stateClone,
    selectedFinanceTypeId: finalSelectedFinanceId,
    selectedCompanyId: undefined,
  };

  return stateClone;
}

function handleFinanceCompanyChange(state: FinancingTypesState, selectedCompanyId: string | undefined): FinancingTypesState {
  //Do not edit provided state directly! State should be immutable!
  //Clone state and do changes there!
  let stateClone = DeepClone(state);
  const finalSelectedCompanyId = selectedCompanyId;
  const selectedFinanceType = getSelectedFinanceType(stateClone);
  if (selectedFinanceType && finalSelectedCompanyId) {
    const selectedCompany = selectedFinanceType.companies?.find(x => x.id === finalSelectedCompanyId);
    if (selectedCompany) {
      selectedCompany.selected = true;
    }

    // reset selected flag for other companies in selected finance type
    selectedFinanceType.companies?.filter(x => x.id !== finalSelectedCompanyId)?.forEach(x => (x.selected = false));

    // reset selected flag for other companies in other finance types
    stateClone?.financingTypes
      ?.filter(x => x.id !== selectedFinanceType?.id)
      ?.forEach(x => x.companies?.forEach(y => (y.selected = false)));

    //setSelectedCompany(stateClone, selectedFinanceType);
  }

  if (!stateClone.isFinanceCompanyValid) {
    stateClone = {
      ...stateClone,
      isFinanceCompanyValid: true,
      selectedCompanyId: finalSelectedCompanyId,
    };
  }

  stateClone = {
    ...stateClone,
    selectedCompanyId: finalSelectedCompanyId,
  };

  return stateClone;
}

function getFinanceTypeById(state: FinancingTypesState, financeId: string | undefined): IFinancingType | undefined {
  return state.financingTypes?.find(x => x.id === financeId);
}

function getSelectedFinanceType(state: FinancingTypesState): IFinancingType | undefined {
  return state.financingTypes?.find(x => x.selected === true);
}

function setSelectedFinancingType(state: FinancingTypesState, selectedFinanceType: IFinancingType) {
  selectedFinanceType.selected = true;
  state.financingTypes?.filter(x => x.id !== selectedFinanceType?.id).forEach(x => (x.selected = false));
}

//function setSelectedCompany(state: FinancingTypeState, selectedCompanyId: string) {}
